"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2, Mail, Phone } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
export default function EstimateRequestForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    zip: "",
    projectType: "Whole-Home",
    timing: "",
    budget: "",
    message: "",
    consent: false
  });
  const [zipError, setZipError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 5);
    setFormData(prev => ({
      ...prev,
      zip: val
    }));
    if (zipError && val.length === 5) {
      setZipError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(false);
    if (!/^\d{5}$/.test(formData.zip)) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      return;
    }
    setZipError("");
    if (!formData.consent) {
      return;
    }
    setIsSubmitting(true);
    const params = new URLSearchParams({
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      project_type: formData.projectType,
      address: formData.zip,
      zip: formData.zip,
      details: formData.message,
      timing: formData.timing,
      budget: formData.budget,
      contact_method: "Phone",
      source: "Brookhaven Elevation website",
      page: typeof window !== "undefined" ? window.location.href : "",
      form_location: "whole-home-remodeling-page"
    });
    try {
      if (!LEAD_WEBHOOK_URL) {
        throw new Error("Webhook endpoint not configured");
      }
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-8 md:mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-3">
            Request an Estimate
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            Tell us about your home and remodeling goals to begin your custom project consultation.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-border bg-card/85 backdrop-blur-md p-6 sm:p-10 shadow-xl">
          {isSuccess ? <div className="text-center py-10 space-y-4">
              <div className="inline-flex items-center justify-center size-14 rounded-full bg-primary/20 text-primary mb-2">
                <CheckCircle2 className="size-8" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground">
                Thank you for your request
              </h3>
              <p className="text-muted-foreground text-base max-w-md mx-auto">
                We have received your project details and will be in touch shortly to discuss next steps.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {submitError && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Unable to submit request</AlertTitle>
                  <AlertDescription>
                    Please call{" "}
                    <a href="tel:(470) 847-9076" className="font-medium underline underline-offset-2 hover:opacity-80">
                      (470) 847-9076
                    </a>{" "}
                    or email{" "}
                    <a href="mailto:serviceleadalerts@gmail.com" className="font-medium underline underline-offset-2 hover:opacity-80">
                      serviceleadalerts@gmail.com
                    </a>{" "}
                    instead.
                  </AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="name" name="name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone" name="phone" type="tel" required placeholder="(404) 555-0123" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email" name="email" type="email" required placeholder="jane@example.com" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip" name="zip" type="text" required placeholder="30319" value={formData.zip} onChange={handleZipChange} aria-invalid={!!zipError} />
                  {zipError && <p className="text-xs text-destructive mt-1">{zipError}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="projectType">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="projectType" name="projectType" value={formData.projectType} onChange={e => setFormData({
                ...formData,
                projectType: e.target.value
              })} required className="border-input focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground [&>option]:bg-card [&>option]:text-card-foreground">
                    <option value="Kitchen">Kitchen</option>
                    <option value="Bathroom">Bathroom</option>
                    <option value="Whole-Home">Whole-Home</option>
                    <option value="Home Addition">Home Addition</option>
                    <option value="Basement">Basement</option>
                    <option value="Interior">Interior</option>
                    <option value="Outdoor Living">Outdoor Living</option>
                    <option value="Deck">Deck</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timing">Preferred Timing</Label>
                  <Select value={formData.timing} onValueChange={val => setFormData({
                ...formData,
                timing: val
              })}>
                    <SelectTrigger id="timing" className="w-full">
                      <SelectValue placeholder="Select timeframe" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="As soon as possible">
                        As soon as possible
                      </SelectItem>
                      <SelectItem value="1-3 months">1-3 months</SelectItem>
                      <SelectItem value="3-6 months">3-6 months</SelectItem>
                      <SelectItem value="6+ months / just exploring">
                        6+ months / just exploring
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Budget Range</Label>
                  <Select value={formData.budget} onValueChange={val => setFormData({
                ...formData,
                budget: val
              })}>
                    <SelectTrigger id="budget" className="w-full">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                      <SelectItem value="$25,000-$75,000">
                        $25,000-$75,000
                      </SelectItem>
                      <SelectItem value="$75,000-$150,000">
                        $75,000-$150,000
                      </SelectItem>
                      <SelectItem value="$150,000-$300,000">
                        $150,000-$300,000
                      </SelectItem>
                      <SelectItem value="$300,000+">$300,000+</SelectItem>
                      <SelectItem value="Prefer not to say">
                        Prefer not to say
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Project Details</Label>
                <Textarea id="message" name="message" placeholder="Tell us about the scope, style, or specific spaces you want to elevate..." value={formData.message} onChange={e => setFormData({
              ...formData,
              message: e.target.value
            })} className="min-h-24" />
              </div>

              <div className="flex items-start gap-3 pt-2">
                <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => setFormData({
              ...formData,
              consent: checked === true
            })} required />
                <Label htmlFor="consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                  I agree to be contacted about my project by phone, text, or email.
                </Label>
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={isSubmitting || !formData.consent} className="w-full h-11 text-base font-medium shadow-md transition-transform active:scale-[0.99]">
                  {isSubmitting ? <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      Submitting request...
                    </> : "Submit Estimate Request"}
                </Button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-6 pt-4 border-t border-border text-xs text-muted-foreground">
                <div className="inline-flex items-center gap-1.5">
                  <Phone className="size-3.5 text-primary" />
                  <a href="tel:(470) 847-9076" className="hover:text-foreground transition-colors">
                    (470) 847-9076
                  </a>
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <Mail className="size-3.5 text-primary" />
                  <a href="mailto:serviceleadalerts@gmail.com" className="hover:text-foreground transition-colors">
                    serviceleadalerts@gmail.com
                  </a>
                </div>
              </div>
            </form>}
        </motion.div>
      </div>
    </section>;
}